import React from 'react';
import { ChevronRight, FileText, Tag } from 'lucide-react';
import { ExportMenu } from '../../ExportMenu';
import { LoadingSpinner } from '../../shared/LoadingSpinner';
import { SmartTable } from '../../SmartTable';
import { type VirtualTableColumn } from '../../VirtualTable';
import { InspectionTagBadge } from '../../inspection/InspectionTagBadge';
import { QuickTagInput } from '../../inspection/QuickTagInput';
import { formatInspectionDateShort } from '../../../utils/timezone';
import { useCarrierTabsTagging } from '../hooks/useCarrierTabsTagging';
import type {
  CarrierProfile as CarrierProfileType,
  Inspection,
} from '../../../types/api';

// Semi-Truck Icon
const TruckIcon = ({ size = 18, color = '#34312c' }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 256 256" xmlns="http://www.w3.org/2000/svg">
    <g fill={color}>
      <path d="M43.5,94.1c0-4.1,0-8.3,0-12.4c0-2.9-1.3-4.2-4.2-4.3c-2.7,0-5.4,0-8.2,0c-2.9,0-4.2,1.3-4.3,4.2c0,8.4,0,16.7,0,25c0,2.8,1.3,4.2,4.2,4.3c2.6,0.1,5.1,0,7.7,0c3.5,0,4.8-1.3,4.8-4.7C43.5,102.2,43.5,98.2,43.5,94.1z M229.2,94.2c0-4.2,0-8.4,0-12.6c0-2.7-1.4-4-4.1-4.1c-2.8-0.1-5.6,0-8.4,0c-2.8,0-4.1,1.4-4.1,4.3c0,8.3,0,16.7,0,25c0,2.9,1.3,4.2,4.2,4.2c2.6,0,5.3,0,7.9,0c3.3,0,4.5-1.3,4.6-4.7C229.2,102.3,229.2,98.2,229.2,94.2z"/>
      <path d="M229.2,215.4c0-7.6-1.1-9.2-8.4-11.7c0-1,0-2,0-3.1c0-10.1,0-20.2,0-30.3c0-7.6-5-14.1-12.1-16.1c-1.5-0.4-3.1-0.6-4.8-0.9c0-1,0-2,0-2.9c0-42.9,0-85.8,0-128.7c0-3-1-4.7-3.7-5.9c-3.4-1.4-6.6-3.3-9.9-4.9c-2.8-1.4-3.1-1.2-3.1,2c0,14.1,0,28.1,0,42.2c0,1,0,2,0,2.9c-2.7-7.5-6.7-13.9-12.3-19.3c-2.5-2.3-5.3-3.5-8.8-3.5c-25.4,0.1-50.8,0.1-76.2,0c-3.4,0-6.2,1-8.6,3.3c-1.6,1.5-3.1,3.1-4.5,4.8c-3.5,4.4-6.1,9.2-8.1,14.6c0-1,0-1.9,0-2.9c0-14.1,0-28.1,0-42.2c0-3.3-0.3-3.4-3.3-2c-3.3,1.6-6.6,3.4-9.9,4.9c-2.6,1.1-3.5,2.9-3.5,5.7c0.1,42.8,0,85.6,0,128.4c0,1.1,0,2.2,0,3.3c-12,2-16.9,7.8-16.9,19.6c0,9.4,0,18.8,0,28.2c0,0.9,0,1.9,0,2.7c-6.7,1.8-8.5,4.1-8.4,10.8c0,1.9,0,3.7,0,5.5c0,5.6,3.4,9,9.1,9.1c2.5,0,5,0,7.8,0c0,2.8,0,5.4,0,7.9c0,5.4,3.5,8.9,8.9,9c5.2,0,10.4,0,15.6,0c5.6,0,9-3.4,9.1-9c0-2.5,0-5.1,0-7.6c34,0,67.7,0,101.5,0c0,2.8,0,5.3,0,7.9c0.1,5.3,3.5,8.7,8.8,8.8c5.3,0,10.5,0,15.8,0c5.4,0,8.9-3.6,8.9-8.9c0-2.5,0-5.1,0-7.9c3,0,5.7,0,8.3,0c4.9-0.1,8.4-3.6,8.6-8.5C229.2,218.9,229.2,217.1,229.2,215.4z M77.5,192.4c0,2.2-1,3.2-3.2,3.2c-6.4,0-12.8,0-19.2,0c-2,0-3.1-0.9-3.1-3c0-3.7,0-7.4,0-11.1c0-2.1,1-3,3.1-3c3.2,0,6.3,0,9.5,0c3.3,0,6.5,0,9.8,0c2.2,0,3.3,0.9,3.2,3.2C77.4,185.2,77.4,188.9,77.5,192.4z M157.8,195.6c-19.9,0-39.7,0-59.6,0c-3.7,0-4.1-0.4-4.1-4.1c0-17.1,0-34.1,0-51.2c0-3.7,0.4-4.1,4.1-4.1c9.9,0,19.9,0,29.8,0c10,0,20,0,30.1,0c3.3,0,3.8,0.5,3.8,3.8c0,17.1,0,34.3,0,51.4C161.9,195.2,161.5,195.6,157.8,195.6z M175.3,111.2c-15.8,0-31.6,0-47.4,0c-15.6,0-31.3,0-46.9,0c-3.6,0-3.7,0-3.7-3.7c0-9,0-17.9,0-26.9c0-2.5,0.6-3.4,3.3-3.4c31.7,0.1,63.4,0.1,95.1,0c2.4,0,3.2,0.8,3.2,3.2c-0.1,9.1,0,18.3,0,27.4C178.7,111.1,178.6,111.2,175.3,111.2z M204.1,192.4c0,2.2-1,3.2-3.2,3.2c-3.2,0-6.3,0-9.5,0s-6.3,0-9.5,0c-2.3,0-3.4-0.9-3.4-3.3c0.1-3.6,0-7.2,0-10.8c0-2.1,1-3.1,3.1-3c6.5,0,13,0,19.5,0c2.1,0,3,1.1,3,3.1C204,185.2,204,188.8,204.1,192.4z"/>
      <path d="M128,153.2c-7.6,0-15.1,0-22.7,0c-1.9,0-2.6-0.4-2.6-2.5c0-5.8-0.1-5.8,5.8-5.8c13.9,0,27.8,0,41.6,0c2.2,0,3.1,0.4,3.1,2.8c-0.1,5.4,0,5.4-5.4,5.4C141.2,153.2,134.6,153.2,128,153.2z"/>
      <path d="M128,161.9c7.6,0,15.1,0.1,22.7,0c2,0,2.5,0.6,2.5,2.6c0,5.7,0,5.7-5.7,5.7c-14,0-27.9,0-41.9,0c-2.4,0-3-0.7-2.9-3c0.2-5.3,0-5.3,5.3-5.3C114.7,161.9,121.4,161.9,128,161.9z"/>
      <path d="M128.3,178.7c7.5,0,14.9,0,22.4,0c1.9,0,2.6,0.5,2.6,2.5c0,5.7,0,5.8-5.6,5.8c-14.1,0-28.1,0-42.2,0c-2.1,0-2.7-0.6-2.7-2.6c0.1-5.6,0-5.6,5.5-5.6C114.9,178.7,121.6,178.7,128.3,178.7z"/>
    </g>
  </svg>
);

// Trailer Icon
const TrailerIcon = ({ size = 18, color = '#857f73' }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 256 256" xmlns="http://www.w3.org/2000/svg">
    <g transform="translate(1.4 1.4) scale(2.81)" fill={color}>
      <path d="M 12.546 63.355 c -3.541 0 -6.421 -2.881 -6.421 -6.421 c 0 -3.541 2.881 -6.422 6.421 -6.422 s 6.421 2.881 6.421 6.422 C 18.967 60.475 16.086 63.355 12.546 63.355 z M 12.546 53.513 c -1.887 0 -3.421 1.535 -3.421 3.422 s 1.535 3.421 3.421 3.421 s 3.421 -1.534 3.421 -3.421 S 14.433 53.513 12.546 53.513 z"/>
      <path d="M 26.423 63.355 c -3.541 0 -6.421 -2.881 -6.421 -6.421 c 0 -3.541 2.881 -6.422 6.421 -6.422 s 6.421 2.881 6.421 6.422 C 32.844 60.475 29.963 63.355 26.423 63.355 z M 26.423 53.513 c -1.887 0 -3.421 1.535 -3.421 3.422 s 1.535 3.421 3.421 3.421 s 3.421 -1.534 3.421 -3.421 S 28.31 53.513 26.423 53.513 z"/>
      <path d="M 82.088 63.355 h -2.124 c -0.828 0 -1.5 -0.672 -1.5 -1.5 v -4.921 c 0 -0.828 0.672 -1.5 1.5 -1.5 s 1.5 0.672 1.5 1.5 v 3.421 h 0.624 c 0.828 0 1.5 0.672 1.5 1.5 S 82.916 63.355 82.088 63.355 z"/>
      <path d="M 40.299 63.355 c -3.541 0 -6.421 -2.881 -6.421 -6.421 c 0 -3.541 2.881 -6.422 6.421 -6.422 s 6.421 2.881 6.421 6.422 C 46.721 60.475 43.84 63.355 40.299 63.355 z M 40.299 53.513 c -1.887 0 -3.421 1.535 -3.421 3.422 s 1.535 3.421 3.421 3.421 s 3.421 -1.534 3.421 -3.421 S 42.186 53.513 40.299 53.513 z"/>
      <path d="M 85.928 26.645 H 4.072 C 1.827 26.645 0 28.471 0 30.716 v 23.646 c 0 2.245 1.827 4.072 4.072 4.072 h 0.194 c -0.088 -0.488 -0.142 -0.987 -0.142 -1.5 c 0 -4.644 3.778 -8.422 8.421 -8.422 c 2.877 0 5.419 1.452 6.938 3.659 c 1.52 -2.207 4.062 -3.659 6.938 -3.659 c 2.877 0 5.418 1.452 6.938 3.659 c 1.52 -2.207 4.062 -3.659 6.938 -3.659 c 4.644 0 8.421 3.778 8.421 8.422 c 0 0.513 -0.053 1.012 -0.142 1.5 H 88.5 c 0.828 0 1.5 -0.672 1.5 -1.5 V 30.716 C 90 28.471 88.173 26.645 85.928 26.645 z"/>
    </g>
  </svg>
);

export interface InspectionsProps {
  dotNumber: number;
  profile: CarrierProfileType;
  inspections: Inspection[];
  totalInspections: number | null;
  loadingMore: { [key: string]: boolean };
  onLoadMoreInspections: () => void;
  setSelectedInspection: (inspection: Inspection | null) => void;
  setTimelineVin: (vin: string | null) => void;
}

const Inspections: React.FC<InspectionsProps> = ({
  dotNumber,
  profile,
  inspections,
  totalInspections,
  loadingMore,
  onLoadMoreInspections,
  setSelectedInspection,
  setTimelineVin,
}) => {
  // Tagging state + handlers (extracted hook)
  const {
    inspectionTags,
    availableShipperTags,
    bulkSelectMode,
    setBulkSelectMode,
    selectedInspections,
    setSelectedInspections,
    bulkTagging,
    tagFilter,
    setTagFilter,
    showTagInputFor,
    setShowTagInputFor,
    handleAddTag,
    handleRemoveTag,
    handleBulkTag,
    toggleInspectionSelection,
    getTagColor,
    filteredInspections,
  } = useCarrierTabsTagging(dotNumber, inspections);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xl)' }}>
      {/* Controls: Export, Tags, Bulk Select */}
      <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        {/* Left: Tag Filter */}
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: '#857f73', marginBottom: '4px' }}>
              FILTER BY TAG
            </label>
            <select
              value={tagFilter || ''}
              onChange={(e) => setTagFilter(e.target.value || null)}
              style={{
                padding: '6px 10px',
                borderRadius: '6px',
                border: '1px solid #d3ccbc',
                fontSize: '0.875rem',
                minWidth: '150px'
              }}
            >
              <option value="">All Inspections</option>
              {Object.keys(inspectionTags).length > 0 &&
                Array.from(new Set(Object.values(inspectionTags).flat())).sort().map((tag) => (
                  <option key={tag} value={tag}>{tag}</option>
                ))}
            </select>
          </div>

          {/* Bulk Select Toggle */}
          <button
            onClick={() => {
              setBulkSelectMode(!bulkSelectMode);
              if (bulkSelectMode) {
                setSelectedInspections(new Set());
              }
            }}
            className={bulkSelectMode ? 'btn-primary' : 'btn-secondary'}
            style={{ fontSize: '0.875rem', marginTop: '20px' }}
          >
            {bulkSelectMode ? `✓ Bulk Select (${selectedInspections.size})` : 'Bulk Select'}
          </button>

          {/* Bulk Tag Button */}
          {bulkSelectMode && selectedInspections.size > 0 && (
            <div style={{ marginTop: '20px' }}>
              <button
                onClick={() => {
                  const tag = prompt('Enter tag name for selected inspections:');
                  if (tag) handleBulkTag(tag);
                }}
                disabled={bulkTagging}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#34312c',
                  color: 'white',
                  borderRadius: '6px',
                  fontSize: '0.875rem'
                }}
              >
                {bulkTagging ? 'Tagging...' : `Tag ${selectedInspections.size} Selected`}
              </button>
            </div>
          )}
        </div>

        {/* Right: Export */}
        <div style={{ marginTop: '20px' }}>
          <ExportMenu
            dataType="inspections"
            data={inspections}
            carrierName={profile?.legalName}
            dotNumber={profile?.dotNumber}
            buttonText="Export Inspections"
            size="sm"
          />
        </div>
      </div>

      {/* Info Banner */}
      <div className="card-modern fade-in-up" style={{ borderLeft: '4px solid var(--color-primary)' }}>
        <div style={{ display: 'flex', gap: 'var(--space-md)', alignItems: 'flex-start' }}>
          <FileText style={{ width: 24, height: 24, color: 'var(--color-primary)', flexShrink: 0 }} />
          <div>
            <h4 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--color-gray-900)', marginBottom: 'var(--space-sm)' }}>
              {totalInspections !== null
                ? `${totalInspections} Total Inspection Records`
                : `${inspections.length} Inspection Records`}
            </h4>
            <p className="text-muted">
              Complete inspection history including shipper names - critical for pattern detection.
              Note: DOT inspection data does NOT include VINs (only crash data has VINs).
            </p>
          </div>
        </div>
      </div>

      {inspections.length > 0 ? (
        <>
          <div className="card-modern fade-in-up" style={{ animationDelay: '0.1s', padding: 0, overflow: 'hidden' }}>
            <SmartTable
              data={filteredInspections}
              columns={[
                ...(bulkSelectMode ? [{
                  key: 'select',
                  header: (
                    <input
                      type="checkbox"
                      checked={selectedInspections.size === filteredInspections.length && filteredInspections.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedInspections(new Set(filteredInspections.map(i => i.inspectionId as string)));
                        } else {
                          setSelectedInspections(new Set());
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                  ) as unknown as string,
                  width: 40,
                  render: (insp: Inspection) => (
                    <input
                      type="checkbox"
                      checked={selectedInspections.has(insp.inspectionId as string)}
                      onChange={() => toggleInspectionSelection(insp.inspectionId as string)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ cursor: 'pointer' }}
                    />
                  )
                }] : []),
                {
                  key: 'insp_date',
                  header: 'Date',
                  width: 150,
                  sortable: true,
                  render: (insp) => {
                    const state = (insp as Inspection & { reportState?: string }).reportState || 'NY';
                    return (
                      <span style={{ fontSize: '0.875rem' }}>
                        {formatInspectionDateShort(insp.inspDate || '', state)}
                      </span>
                    );
                  }
                },
                {
                  key: 'inspection_id',
                  header: 'Inspection #',
                  width: 130,
                  render: (insp) => (
                    <span
                      onClick={(e) => { e.stopPropagation(); setSelectedInspection(insp); }}
                      style={{
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        color: 'var(--color-primary)',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        fontWeight: 600
                      }}
                    >
                      {insp.inspectionId || '-'}
                    </span>
                  )
                },
                {
                  key: 'units',
                  header: 'Units',
                  width: 60,
                  className: 'centered',
                  render: (insp) => {
                    const vins = insp.vins;
                    return vins && vins.length > 0 ? (
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', alignItems: 'center' }}>
                        <TruckIcon size={18} color="#34312c" />
                        {vins.length > 1 && <TrailerIcon size={18} color="#857f73" />}
                      </div>
                    ) : null;
                  }
                },
                {
                  key: 'primaryVin',
                  header: 'VIN (Truck)',
                  width: 150,
                  render: (insp) => {
                    const primaryVin = insp.primaryVin;
                    return primaryVin ? (
                      <span
                        onClick={(e) => { e.stopPropagation(); setTimelineVin(primaryVin); }}
                        style={{
                          fontFamily: 'monospace',
                          fontSize: '0.875rem',
                          color: 'var(--color-primary)',
                          cursor: 'pointer',
                          textDecoration: 'underline'
                        }}
                      >
                        {primaryVin}
                      </span>
                    ) : <span style={{ fontFamily: 'monospace', fontSize: '0.875rem', color: 'var(--color-gray-400)' }}>-</span>;
                  }
                },
                {
                  key: 'reportState',
                  header: 'State',
                  width: 60,
                  sortable: true,
                  className: 'bold'
                },
                {
                  key: 'inspLevelId',
                  header: 'Level',
                  width: 80,
                  render: (insp) => {
                    return (
                      <span className="badge-modern badge-info" style={{ fontSize: '0.75rem' }}>
                        Level {insp.inspLevelId}
                      </span>
                    );
                  }
                },
                {
                  key: 'locationDesc',
                  header: 'Location',
                  width: 150,
                  render: (insp) => (insp as Inspection & { locationDesc?: string }).locationDesc || 'N/A'
                },
                {
                  key: 'violTotal',
                  header: 'Violations',
                  width: 80,
                  sortable: true,
                  className: 'centered bold',
                  render: (insp) => {
                    const violTotal = (insp as Inspection & { violTotal?: number }).violTotal || 0;
                    return (
                      <span style={{ color: violTotal > 0 ? 'var(--color-warning)' : 'var(--color-success)' }}>
                        {violTotal}
                      </span>
                    );
                  }
                },
                {
                  key: 'oosTotal',
                  header: 'OOS',
                  width: 80,
                  sortable: true,
                  render: (insp) => {
                    const oosTotal = (insp as Inspection & { oosTotal?: number }).oosTotal || 0;
                    return oosTotal > 0 ? (
                      <span className="badge-modern badge-danger" style={{ fontSize: '0.75rem' }}>OOS: {oosTotal}</span>
                    ) : (
                      <span className="badge-modern badge-success" style={{ fontSize: '0.75rem' }}>Clean</span>
                    );
                  }
                },
                {
                  key: 'shipperName',
                  header: 'Shipper',
                  width: 150,
                  render: (insp) => (
                    <span style={{ fontSize: '0.875rem', color: 'var(--color-gray-600)' }}>
                      {(insp as Inspection & { shipperName?: string }).shipperName || '-'}
                    </span>
                  )
                },
                {
                  key: 'shippingPaperNumber',
                  header: 'BOL#',
                  width: 120,
                  render: (insp) => (
                    <span style={{ fontSize: '0.875rem', color: 'var(--color-gray-600)', fontFamily: 'monospace' }}>
                      {(insp as Inspection & { shippingPaperNumber?: string }).shippingPaperNumber || '-'}
                    </span>
                  )
                },
                {
                  key: 'tags',
                  header: 'Tags',
                  width: 300,
                  render: (insp) => {
                    const inspId = insp.inspectionId as string;
                    const tags = inspectionTags[inspId] || [];
                    const isAddingTag = showTagInputFor === inspId;

                    return (
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center', minHeight: '32px' }} onClick={(e) => e.stopPropagation()}>
                        {tags.map((tag) => (
                          <InspectionTagBadge
                            key={tag}
                            tagName={tag}
                            color={getTagColor(tag)}
                            onRemove={() => handleRemoveTag(inspId, tag)}
                          />
                        ))}

                        {isAddingTag ? (
                          <QuickTagInput
                            onTagAdd={(tagName) => {
                              handleAddTag(inspId, tagName);
                              setShowTagInputFor(null);
                            }}
                            onCancel={() => setShowTagInputFor(null)}
                            suggestedTags={availableShipperTags.map(t => t.tagName)}
                            autoFocus={true}
                          />
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowTagInputFor(inspId);
                            }}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '4px 8px',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              border: '1px solid #d3ccbc',
                              borderRadius: '6px',
                              background: '#fbf9f4',
                              color: '#857f73',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              flexShrink: 0
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#f5f2ea';
                              e.currentTarget.style.borderColor = '#23211d';
                              e.currentTarget.style.color = '#23211d';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'white';
                              e.currentTarget.style.borderColor = '#d3ccbc';
                              e.currentTarget.style.color = '#857f73';
                            }}
                          >
                            <Tag size={12} />
                            + Tag
                          </button>
                        )}
                      </div>
                    );
                  }
                }
              ] as VirtualTableColumn<Inspection>[]}
              keyExtractor={(insp, idx) => insp.inspectionId || idx.toString()}
              rowHeight={60}
              height="auto"
              virtualThreshold={100}
              sortable={true}
              stickyHeader={true}
              onRowClick={(insp) => setSelectedInspection(insp)}
              getRowClassName={(insp) => (insp.oosTotal || 0) > 0 ? 'danger' : ''}
              emptyMessage="No inspection records found"
            />
          </div>

        {/* Load More Button for Inspections */}
        {(() => {
          const total = totalInspections;
          const hasMore = total === null || inspections.length < total;
          const allLoaded = total !== null && inspections.length >= total;

          return (
            <div style={{ marginTop: 'var(--space-lg)' }}>
              {/* Pagination Info */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 'var(--space-sm)',
                fontSize: '0.875rem',
                color: 'var(--color-gray-600)'
              }}>
                <span>
                  Showing {inspections.length}{total !== null ? ` of ${total}` : ''} inspections
                </span>
                {allLoaded && (
                  <span style={{ color: 'var(--color-success)', fontWeight: 500 }}>
                    All inspections loaded
                  </span>
                )}
              </div>

              {/* Load More Button - only show if more data available */}
              {hasMore && (
                <button
                  onClick={() => onLoadMoreInspections()}
                  disabled={loadingMore['inspections']}
                  className="btn-secondary"
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 'var(--space-sm)',
                  }}
                >
                  {loadingMore['inspections'] ? (
                    <>
                      <LoadingSpinner size="sm" />
                      Loading more...
                    </>
                  ) : (
                    <>
                      <ChevronRight size={18} />
                      Load More Inspections
                    </>
                  )}
                </button>
              )}
            </div>
          );
        })()}
        </>
      ) : (
        <div className="card-modern" style={{ textAlign: 'center', padding: 'var(--space-lg)' }}>
          <FileText style={{ width: 40, height: 40, color: 'var(--color-gray-300)', margin: '0 auto var(--space-lg)' }} />
          <p className="text-muted" style={{ fontSize: '1rem' }}>No inspection records found.</p>
        </div>
      )}
    </div>
  );
};

export default Inspections;
